import { useState } from 'react';
import type { BackorderRow, NomenclatureDto } from '@eombor/contracts';
import { Button, Dialog, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { NomenclaturePicker } from '../../catalog/ui/NomenclaturePicker.js';
import { useBackorderAction } from '../model/backorder.queries.js';

const FALLBACK = "Amalni bajarib bo'lmadi";

const errorText = (error: unknown): string =>
  error instanceof ApiClientError ? error.message : FALLBACK;

/**
 * QABUL QILISH yoki RAD ETISH — bitta oynada.
 *
 * Ikkalasi bitta qarorning ikki tomoni: skladchi arizani o'qib, «olib
 * kelaman» yoki «yo'q» deydi. Ikki alohida oyna bo'lsa, rad etish
 * ko'rinmaydigan joyda qolib, skladchi javobsiz arizani navbatda ushlab
 * turishga odatlanardi.
 *
 * Erkin matnli bildirgida katalog tanlash IXTIYORIY: skladchi mahsulotni
 * katalogdan har doim topa olmaydi va shu sababli arizani ushlab
 * turmasligi kerak. Bog'lanmagan bildirgi uchun mahsulot biriktirish
 * paytida, omborda bor narsadan tanlanadi.
 */
export const AcceptBackorderDialog = ({
  row,
  onClose,
}: {
  row: BackorderRow;
  onClose: () => void;
}) => {
  const needsBinding = row.nomenclatureId === null;
  const [nomenclature, setNomenclature] = useState<NomenclatureDto | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const act = useBackorderAction();

  const canReject = reason.trim().length >= 5;

  const accept = (): void => {
    if (act.isPending) return;
    act.mutate(
      { id: row.id, action: 'accept', nomenclatureId: nomenclature?.id ?? null },
      { onSuccess: onClose },
    );
  };

  const reject = (): void => {
    if (!canReject || act.isPending) return;
    act.mutate({ id: row.id, action: 'reject', reason: reason.trim() }, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={rejecting ? t('backorder.rejectTitle') : t('backorder.acceptTitle')}
      description={`${row.docNo} · ${row.requesterName}`}
      busy={act.isPending}
      onSubmit={rejecting ? reject : accept}
      error={act.isError ? errorText(act.error) : undefined}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={act.isPending}>
            {t('backorder.cancel')}
          </Button>
          {rejecting ? (
            <Button variant="danger" onClick={reject} disabled={!canReject} loading={act.isPending}>
              {t('backorder.reject')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setRejecting(true)} disabled={act.isPending}>
                {t('backorder.rejectSwitch')}
              </Button>
              <Button onClick={accept} loading={act.isPending}>
                {t('backorder.accept')}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3">
          <p className="text-sm font-medium text-text">
            {row.nomenclatureName ?? row.freeText}
          </p>
          <p className="tabular mt-1 text-sm text-text-muted">
            {row.requestedQty} {row.unit ?? ''} · {row.warehouseName}
          </p>
          <p className="mt-2 border-l-2 border-accent/40 pl-2 text-sm text-text-muted italic">
            {row.purpose}
          </p>
        </div>

        {rejecting ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-text">{t('backorder.rejectReason')}</span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('backorder.rejectReasonPlaceholder')}
              className={cn(
                'resize-y rounded-[var(--radius-sm)] border border-border-control bg-surface',
                'px-3 py-2 text-base text-text hover:border-border-strong',
              )}
            />
            {/* Sabab buyurtmachiga BORADI: usiz u nima qilishini bilmaydi. */}
            <span className="text-xs text-text-muted">{t('backorder.rejectReasonHint')}</span>
          </label>
        ) : needsBinding ? (
          <NomenclaturePicker
            value={nomenclature}
            onSelect={setNomenclature}
            label={t('backorder.bind')}
            hint={t('backorder.bindHint')}
          />
        ) : null}
      </div>
    </Dialog>
  );
};
